import { DISCOUNT_TYPES } from "constants/constants";
import {
  calculatePercentageValue,
  renderPriceWithSymbol,
  templateLiteralPolyfill,
} from "utils/commonMethods";
import useCartItem from "hooks/useCartItem";

function useDiscount(item) {
  const componentTexts = window.appTexts.common_texts.discount_messages;
  const { itemQuantity } = useCartItem(item);

  const hasDiscount = item.dscnt.length === 1;

  let response = {
    hasDiscount,
    discountPrice: 0,
    discountPerQuantity: 0,
    applyDiscountMessage: "",
    discountTitle: "",
    discountSavingMessage: "",
  };

  //If no discount is available, return no discount found.
  if (!hasDiscount) {
    return response;
  }

  const discount = item.dscnt[0];

  //Destructuring discount values.
  const {
    dTyp: discountType,
    P: discountValue,

    X: discountBuyValue,
    Y: discountGetValue,
  } = discount;
  let { maxQty: discountMaxQuantity } = discount;
  if (!discountMaxQuantity) {
    discountMaxQuantity = Number.MAX_SAFE_INTEGER;
  }

  switch (discountType) {
    case DISCOUNT_TYPES.FLAT: {
      //Discount title
      response.discountTitle = templateLiteralPolyfill(
        componentTexts.discount_titles.flat_off,
        { price: renderPriceWithSymbol(discountValue) }
      );
      //Discount message to let user know how to apply it.
      response.applyDiscountMessage =
        itemQuantity > discountMaxQuantity
          ? templateLiteralPolyfill(
              componentTexts.apply_discount_msgs.flat_off,
              { quantity: discountMaxQuantity }
            )
          : "";
      //Total discount price for current cart quantity.
      response.discountPrice =
        discountValue *
        (itemQuantity <= discountMaxQuantity
          ? itemQuantity
          : discountMaxQuantity);
      response.discountPerQuantity = discountValue;
      break;
    }
    case DISCOUNT_TYPES.PERCENTAGE: {
      let percentageDiscountAmount = calculatePercentageValue(
        item.val,
        discountValue
      );
      //Discount title
      response.discountTitle = templateLiteralPolyfill(
        componentTexts.discount_titles.percentage_off,
        { percentage: discountValue }
      );
      //Discount message to let user know how to apply it.
      response.applyDiscountMessage =
        itemQuantity > discountMaxQuantity
          ? templateLiteralPolyfill(
              componentTexts.apply_discount_msgs.percentage_off,
              { quantity: discountMaxQuantity }
            )
          : "";
      //Total discount price for current cart quantity.
      response.discountPrice =
        percentageDiscountAmount *
        (itemQuantity <= discountMaxQuantity
          ? itemQuantity
          : discountMaxQuantity);
      response.discountPerQuantity = percentageDiscountAmount;
      break;
    }
    case DISCOUNT_TYPES.BUY_X_FOR_Y: {
      const addItems = discountBuyValue - (itemQuantity % discountBuyValue),
        comboPrice = item.val * discountBuyValue;

      if (discountMaxQuantity > item.stk) {
        break;
      }
      //Discount title
      response.discountTitle = templateLiteralPolyfill(
        componentTexts.discount_titles.buy_x_for_y,
        { x: discountBuyValue, y: renderPriceWithSymbol(discountValue) }
      );

      //Total discount price for current cart quantity.
      response.discountPrice =
        (comboPrice - discountValue) *
        Math.floor(itemQuantity / discountBuyValue);

      //Discount message to let user know how to apply it.
      response.applyDiscountMessage =
        addItems + itemQuantity <= item.stk
          ? templateLiteralPolyfill(
              componentTexts.apply_discount_msgs.buy_x_for_y,
              {
                items: addItems,
                price: renderPriceWithSymbol(comboPrice - discountValue),
              }
            )
          : "";
      break;
    }
    case DISCOUNT_TYPES.BUY_X_GET_Y: {
      if (discountBuyValue + discountGetValue >= item.stk) {
        return response;
      }
      const addItems = discountBuyValue - (itemQuantity % discountBuyValue);
      const extraItems = Math.floor(
        discountGetValue * (itemQuantity / discountBuyValue)
      );
      const cartValueWithExtraItems = extraItems + item.stk;
      //Discount title
      response.discountTitle = templateLiteralPolyfill(
        componentTexts.discount_titles.buy_x_get_y,
        { x: discountBuyValue, y: discountGetValue }
      );

      //Discount message to let user know how to apply it.
      response.applyDiscountMessage = (function () {
        if (itemQuantity < discountBuyValue) {
          return templateLiteralPolyfill(
            componentTexts.apply_discount_msgs.buy_x_get_y.add_x_items,
            { addItems, getItems: discountGetValue }
          );
        } else if (extraItems + itemQuantity <= item.stk) {
          return templateLiteralPolyfill(
            componentTexts.apply_discount_msgs.buy_x_get_y.items_dispense,
            { items: extraItems }
          );
        } else {
          if (extraItems - (cartValueWithExtraItems - item.stk)) {
            return templateLiteralPolyfill(
              componentTexts.apply_discount_msgs.buy_x_get_y
                .partial_stock_issue,
              { items: extraItems - (cartValueWithExtraItems - item.stk) }
            );
          } else {
            return componentTexts.apply_discount_msgs.buy_x_get_y.stock_issue;
          }
        }
      })();
      break;
    }
    default:
      return response;
  }

  //Total savings by user message.
  response.discountSavingMessage = response.discountPrice
    ? templateLiteralPolyfill(componentTexts.discount_saving_msg, {
        value: renderPriceWithSymbol(response.discountPrice),
      })
    : "";

  return response;
}

export default useDiscount;
